/**
 * Link helpers for contact details.
 *
 * A phone number staff can't tap is a phone number they have to retype into
 * the dialer one-handed on a coach in Melbourne, so everything that CAN be a
 * link is one: emails open the mail app, numbers dial, addresses of websites
 * open in a new tab.
 *
 * Two shapes of input, hence two entry points:
 *   - single-value fields (contacts.email, .phone, .website) -> the *Href
 *     helpers, used straight from a :href binding;
 *   - free text and Quill prose (contacts.notes, trips.app_contacts), where
 *     the links are buried inside a sentence -> linkifyText / linkifyHtml.
 *
 * Plain module rather than a mixin, matching how Roger keeps contact_meta.js.
 */

// Matches an email OR a URL. One combined pattern rather than two passes so an
// address is never linked twice (the "@" in a query string used to do that).
// Deliberately conservative: a bare "example.com" is NOT matched -- prose is
// full of sentences ending in a word, and "arrive Tue.Book early" is not a
// domain. http(s):// or a www. prefix is required.
const LINK_RE = /([A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,})|((?:https?:\/\/|www\.)[^\s<>"']+)/g;

// Punctuation that ends a sentence rather than a URL. Closing brackets only
// come off when unbalanced, so a Wikipedia-style "(...)" path survives.
function trimTrailingPunctuation(url) {
    let result = url;
    for (;;) {
        const last = result.slice(-1);
        if ('.,;:!?'.indexOf(last) > -1) {
            result = result.slice(0, -1);
            continue;
        }
        if ((last === ')' || last === ']') && result.split(last === ')' ? '(' : '[').length <= result.split(last).length) {
            result = result.slice(0, -1);
            continue;
        }
        return result;
    }
}

export function escapeHtml(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * Phone numbers are stored normalized with a leading "+". Collapse any number
 * of leading "+" into exactly one so we never render "++1650...".
 */
export function telHref(phone) {
    if (!phone) return '';
    return '+' + String(phone).replace(/^\++/, '');
}

/**
 * Staff type "crowntowers.com.au" as often as they paste a full URL, and a
 * schemeless href resolves against the app and 404s inside Navigator. Assume
 * https for anything without a scheme; leave mailto:/tel:/http: alone.
 */
export function webHref(url) {
    const value = String(url == null ? '' : url).trim();
    if (!value) return '';
    return /^[a-z][a-z0-9+.-]*:/i.test(value) ? value : 'https://' + value;
}

/**
 * Social fields hold anything from "@tennisventures" to a full profile URL.
 * Returns a URL for the handle forms and passes URLs through; null means
 * "render as plain text" rather than guessing wrong.
 */
const SOCIAL_BASE = {
    instagram: 'https://instagram.com/',
    facebook: 'https://facebook.com/',
    twitter: 'https://twitter.com/'
};

export function socialHref(network, handle) {
    const value = String(handle == null ? '' : handle).trim();
    if (!value) return null;
    if (/^(https?:)?\/\//i.test(value) || /^www\./i.test(value)) return webHref(value);

    const name = value.replace(/^@/, '').trim();
    if (!name) return null;

    if (network === 'whatsapp') {
        // wa.me wants digits only; a handle that isn't a number isn't dialable.
        const digits = name.replace(/[^\d]/g, '');
        return digits ? 'https://wa.me/' + digits : null;
    }

    // Anything with whitespace or path characters is a description, not a
    // handle ("ask at the desk") -- render it as text rather than link to a
    // profile that doesn't exist.
    if (!SOCIAL_BASE[network] || /[\s/?#]/.test(name)) return null;

    return SOCIAL_BASE[network] + encodeURIComponent(name);
}

/**
 * Turn the emails and URLs inside a plain-text string into links, returning
 * HTML for v-html. Escapes FIRST, then matches, so the input can never inject
 * markup -- the entities escaping produces ("&amp;") stay inside the match and
 * are correct in both the href and the label.
 */
export function linkifyText(text) {
    const escaped = escapeHtml(text);

    return escaped.replace(LINK_RE, (match, email, url) => {
        if (email) return '<a href="mailto:' + email + '">' + email + '</a>';

        const trimmed = trimTrailingPunctuation(url);
        const tail = url.slice(trimmed.length);
        return '<a href="' + webHref(trimmed) + '" target="_blank" rel="noopener">' + trimmed + '</a>' + tail;
    });
}

/**
 * Same, for prose that is ALREADY HTML (Quill sections such as
 * trips.app_contacts). Walks text nodes only, so existing markup -- including
 * links Quill made itself -- is left exactly as it is; the pass is a no-op on
 * content that was already fully linked.
 */
export function linkifyHtml(html) {
    if (!html) return html;
    if (typeof DOMParser === 'undefined') return html;

    const doc = new DOMParser().parseFromString('<div>' + html + '</div>', 'text/html');
    const root = doc.body.firstChild;
    if (!root) return html;

    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, null, false);
    const targets = [];

    while (walker.nextNode()) {
        const node = walker.currentNode;
        if (!LINK_RE.test(node.nodeValue || '')) {
            LINK_RE.lastIndex = 0;
            continue;
        }
        LINK_RE.lastIndex = 0;

        // Never re-link inside an anchor: that would nest <a> tags.
        let el = node.parentNode, inside = false;
        while (el && el !== root) {
            const tag = (el.tagName || '').toLowerCase();
            if (tag === 'a' || tag === 'script' || tag === 'style') {
                inside = true;
                break;
            }
            el = el.parentNode;
        }
        if (!inside) targets.push(node);
    }

    targets.forEach(node => {
        const span = doc.createElement('span');
        span.innerHTML = linkifyText(node.nodeValue);
        node.parentNode.replaceChild(span, node);
    });

    return root.innerHTML;
}
